import { groupParticipants } from './communicationRecipients';
// BL-241 Gate 2 — pure helpers behind the Communications two-pane redesign.
//
// Frontend-only read/render logic: nothing here writes to intake_sessions, calls an Edge
// Function, or touches the staff-send-reply contract. Same shape as src/lib/staffReply.js —
// plain functions so the merge/tagging/gap rules unit test without React or Supabase.
//
// Two logs feed the thread view and they overlap:
//   - conversation_log: AI Attendant turns ({ role: 'user' | 'assistant' | 'staff', content, at })
//   - raw_message_log:  the provider audit trail ({ direction, channel, body, received_at })
// An inbound text usually lands in both, so entries are merged and de-duplicated on
// direction + message text before rendering.

import { looksLikeHtmlBody, normalizeInboundEmailBody, htmlToPlainText } from './emailBodyText';
import { reconcileAll } from './optimisticMessages';
import { importedBody, LEGACY_IMPORTED_LABEL, LEGACY_IMPORTED_SOURCE } from './legacyImportedMessages';

export const CONVERSATION_GAP_MS = 30 * 60 * 1000; // 30 minutes
export const INBOUND_MESSAGE_MEDIA_BUCKET = 'inbound-message-media';

const INBOUND_ROLES = ['user', 'client', 'customer', 'contact'];

// BL-177 Gate 16: outbound WhatsApp templates were saved with their raw body ("Good {{1}} {{2}}…").
// The thread fills those placeholders from the row's own contentVariables when it has them, and
// shows a quiet "—" for any value that is truly missing — staff never see a raw "{{n}}".
const TEMPLATE_PLACEHOLDER_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;
export const MISSING_TEMPLATE_VALUE = '\u2014';
const TEMPLATE_TWIN_WINDOW_MS = 2 * 60 * 1000;
// Word joiners (invisible) fence the dash so the renderer can tell a missing template value apart
// from an em dash the sender actually typed.
export const MISSING_TEMPLATE_MARKER = `\u2060${MISSING_TEMPLATE_VALUE}\u2060`;

export function hasTemplatePlaceholders(text) {
    return typeof text === 'string' && /\{\{\s*[^{}]+?\s*\}\}/.test(text);
}

/** contentVariables are stored as an object (staff-send-reply) or a JSON string (twilio-send-message). */
export function parseTemplateVariables(value) {
    let parsed = value;
    if (typeof value === 'string') {
        try {
            parsed = JSON.parse(value);
        } catch {
            return null;
        }
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
}

/** Fills every placeholder that has a non-empty value; leaves the rest untouched. */
export function fillTemplatePlaceholders(text, variables = null) {
    if (typeof text !== 'string' || !variables) return typeof text === 'string' ? text : '';
    return text.replace(TEMPLATE_PLACEHOLDER_PATTERN, (placeholder, key) => {
        const value = variables[key];
        if (typeof value === 'number') return String(value);
        if (typeof value === 'string' && value.trim()) return value;
        return placeholder;
    });
}

/** Swaps any placeholder still unfilled for the missing-value marker. */
export function blankUnresolvedPlaceholders(text) {
    if (typeof text !== 'string') return '';
    return text.replace(TEMPLATE_PLACEHOLDER_PATTERN, MISSING_TEMPLATE_MARKER);
}

/** Splits display text into plain runs and missing-template-value runs for rendering. */
export function splitMissingTemplateValues(text) {
    if (typeof text !== 'string' || !text) return [];
    return text.split(MISSING_TEMPLATE_MARKER).flatMap((part, index) => {
        const segments = [];
        if (index > 0) segments.push({ text: MISSING_TEMPLATE_VALUE, missing: true });
        if (part) segments.push({ text: part, missing: false });
        return segments;
    });
}

/** Plain-text form of a display string (marker → ordinary dash), for previews and search. */
export function toPlainThreadText(text) {
    return typeof text === 'string' ? text.split(MISSING_TEMPLATE_MARKER).join(MISSING_TEMPLATE_VALUE) : '';
}

function applyTemplateVariables(entry, rawText) {
    const variables = parseTemplateVariables(entry.contentVariables ?? entry.content_variables);
    return {
        text: fillTemplatePlaceholders(rawText, variables),
        templateText: rawText,
        contentVariables: variables,
    };
}

/**
 * BL-177 Gate 13: user_profiles only guarantees full_name; auth email is the next fallback.
 */
export function resolveStaffDisplayName(profile = {}) {
    const fullName = firstString(profile?.full_name);
    if (fullName) return fullName;
    const email = firstString(profile?.email);
    if (email) return email.split('@')[0];
    return 'Staff';
}

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

function toTimestamp(value) {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

function textDedupeKey(entry) {
    return `${entry.direction}::${entry.text.replace(/\s+/g, ' ').trim().toLowerCase()}::${entry.mediaKeys.join('|')}`;
}

function dedupeKey(entry) {
    if (entry.callSid) return `twilio-call::${entry.callSid}`;
    if (entry.providerEventId) return `imported::${entry.providerEventId}`;
    if (entry.providerMessageId) return `provider::${entry.direction}::${entry.providerMessageId}`;
    return textDedupeKey(entry);
}

// BL-177 Step 2d (live bug Oct 14): a staff conversation_log entry written before client_ref was recorded there pairs
// with the IntelAgent-sent raw row (which has a client_ref) of the same text when they are this close in time.
const STAFF_ECHO_PAIR_WINDOW_MS = 10 * 60 * 1000;

function normalizePhoneAddress(value) {
    if (typeof value !== 'string') return '';
    return value.replace(/^whatsapp:/i, '').trim();
}

function buildMediaEntry(value = {}) {
    if (!value || typeof value !== 'object') return null;
    const bucket = firstString(value.storage_bucket) || INBOUND_MESSAGE_MEDIA_BUCKET;
    const storagePath = firstString(value.storage_path);
    if (storagePath) {
        return {
            key: `storage:${bucket}:${storagePath}`,
            storageBucket: bucket,
            storagePath,
            contentType: firstString(value.contentType, value.content_type) || null,
            sourceUrl: firstString(value.source_url) || null,
            url: firstString(value.url) || null,
        };
    }
    const url = firstString(value.url);
    if (url) return { key: `url:${url}`, url };
    return null;
}

function urlMediaEntry(url) {
    const normalized = firstString(url);
    if (!normalized) return null;
    return { key: `url:${normalized}`, url: normalized };
}

// 1.12.212-beta: an email's subject, its Cc / Bcc and its formatted body ride along on the message (Bcc is staff-only; the
// thread is a staff screen). Empty for every other channel.
function stringList(value) {
    return (Array.isArray(value) ? value : []).map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean);
}
export function emailMessageFields(entry) {
    if (!entry || typeof entry !== 'object' || firstString(entry.channel) !== 'email') return {};
    const subject = firstString(entry.subject, entry.email_subject);
    const cc = stringList(entry.cc);
    const bcc = stringList(entry.bcc);
    const to = stringList(entry.to);
    const html = firstString(entry.html);
    return {
        ...(subject ? { emailSubject: subject } : {}),
        emailSessionId: entry.source_session_id || null,
        emailThreadId: entry.thread_id || entry.conversation_id || entry.gmail_thread_id || null,
        emailMessageId: entry.internet_message_id || entry.message_id || null,
        emailReferences: entry.references || null, emailInReplyTo: entry.in_reply_to || null,
        emailProviderId: entry.email_provider_id || null, emailSyncId: entry.provider_message_id || null,
        emailSyncError: entry.sync_error || null,
        emailFullText: typeof entry.body === 'string' ? (looksLikeHtmlBody(entry.body) ? htmlToPlainText(entry.body) : entry.body) : null,
        emailFrom: entry.from || null, emailIsRead: entry.is_read === true,
        emailDeleted: entry.deleted === true || entry.policy_hidden === true, emailOutsideFolders: entry.outside_folders === true,
        ...(cc.length ? { emailCc: cc } : {}),
        ...(bcc.length ? { emailBcc: bcc } : {}),
        ...(to.length ? { emailTo: to } : {}),
        ...(html ? { emailHtml: html } : {}),
    };
}

/** Normalizes one conversation_log turn (or an optimistic just-sent staff entry). */
export function normalizeConversationEntry(entry, index = 0) {
    if (!entry || typeof entry !== 'object') return null;
    const role = firstString(entry.role, entry.author).toLowerCase() || null;
    const explicitDirection = firstString(entry.direction).toLowerCase();
    const direction = explicitDirection === 'inbound' || explicitDirection === 'outbound'
        ? explicitDirection
        : (role && INBOUND_ROLES.includes(role) ? 'inbound' : 'outbound');
    const mediaEntries = [
        ...(Array.isArray(entry.media) ? entry.media.map((media) => buildMediaEntry(media)) : []),
        ...(Array.isArray(entry.mediaUrl) ? entry.mediaUrl.map((url) => urlMediaEntry(url)) : []),
        ...(typeof entry.mediaUrl === 'string' ? [urlMediaEntry(entry.mediaUrl)] : []),
        ...(typeof entry.media_url === 'string' ? [urlMediaEntry(entry.media_url)] : []),
    ].filter(Boolean);
    const template = applyTemplateVariables(entry, firstString(entry.content, entry.body, entry.text, entry.message));
    return {
        id: `conversation-${index}`,
        source: 'conversation_log',
        tripId: entry.trip_id || null, tripLabel: entry.trip_label || null, isTest: entry.is_test === true,
        appFile: entry.channel === 'app_chat' ? entry.media?.find((file) => file.file_id) || null : null,
        role,
        direction,
        ...template,
        channel: firstString(entry.channel) || null,
        staffName: firstString(entry.staffName, entry.staff_name) || null,
        staffUserId: firstString(entry.staffUserId, entry.staff_user_id, entry.userId, entry.user_id) || null,
        // BL-177 Step 2d: the optimistic bubble and the staff log entry carry the send's client_ref.
        clientRef: firstString(entry.client_ref, entry.clientRef) || null,
        providerMessageId: firstString(entry.provider_message_id, entry.message_id, entry.internet_message_id, entry.providerMessageId) || null,
        provider_status: null,
        provider_error_code: null,
        provider_error_message: null,
        ...emailMessageFields(entry),
        eventId: entry.event_name ? entry.event_id : null,
        eventName: entry.event_name || null,
        at: toTimestamp(entry.at || entry.sent_at || entry.received_at || entry.timestamp || entry.created_at),
        pending: Boolean(entry.pending),
        media: mediaEntries,
        mediaKeys: mediaEntries.map((media) => media.key),
        mediaUrls: mediaEntries.map((media) => media.url).filter(Boolean),
        index,
    };
}

/** Normalizes one raw_message_log entry (the provider audit trail). */
export function normalizeRawEntry(entry, index = 0) {
    if (!entry || typeof entry !== 'object') return null;
    const explicitDirection = firstString(entry.direction).toLowerCase();
    const systemLine = entry.system_line === true;
    const storedBody = firstString(entry.body, entry.content, entry.text, entry.message);
    const rawBody = entry.source === LEGACY_IMPORTED_SOURCE ? importedBody(storedBody) : storedBody;
    const normalizedBody = looksLikeHtmlBody(rawBody)
        ? normalizeInboundEmailBody({ bodyContent: rawBody, bodyContentType: 'html' })
        : rawBody;
    const mediaEntries = [
        ...(Array.isArray(entry.media) ? entry.media.map((media) => buildMediaEntry(media)) : []),
        ...(Array.isArray(entry.media_urls) ? entry.media_urls.map((url) => urlMediaEntry(url)) : []),
        ...(typeof entry.media_url === 'string' ? [urlMediaEntry(entry.media_url)] : []),
    ].filter(Boolean);
    const template = applyTemplateVariables(entry, normalizedBody);
    return {
        id: `raw-${index}`,
        source: 'raw_message_log',
        messageSource: firstString(entry.source) || null,
        providerEventId: firstString(entry.event_id) || null,
        providerMessageId: firstString(
            entry.provider_message_id,
            entry.message_id,
            entry.internet_message_id,
            entry.graph_message_id,
            entry.gmail_message_id,
            entry.provider_sid,
            entry.sid,
        ) || null,
        role: null,
        direction: systemLine ? 'system' : (explicitDirection === 'outbound' ? 'outbound' : 'inbound'),
        systemLine,
        systemKind: firstString(entry.kind) || null,
        ...template,
        channel: firstString(entry.channel) || null,
        // BL-177 Step 2c (F4): a text sent from IntelAgent records who sent it; a text sent from the imported provider's own app may
        // carry the imported provider's sender name. Either way the bubble shows a person, not a bare "Staff".
        staffUserId: firstString(entry.sent_by) || null,
        staffName: firstString(entry.sent_by_name) || null,
        // BL-177 Step 2e (6b): who in a group sent this incoming text (E.164).
        senderPhone: firstString(entry.sender_phone) || null,
        clientRef: firstString(entry.client_ref) || null,
        retried: Boolean(firstString(entry.retried_by_client_ref)),
        provider_status: firstString(entry.provider_status) || null,
        sendingLine: firstString(entry.sending_line, entry.from) || null,
        carrier: entry.carrier || null,
        deliveryReceipts: entry.delivery_receipts,
        provider_error_code: firstString(entry.provider_error_code) || null,
        provider_error_message: firstString(entry.provider_error_message) || null,
        callSid: firstString(entry.call_sid) || null,
        callStatus: firstString(entry.status) || null,
        callDuration: Number.isFinite(Number(entry.duration)) ? Number(entry.duration) : null,
        answeredByUserId: firstString(entry.answered_by_user_id) || null,
        answeredByName: firstString(entry.answered_by_name) || null,
        transcript: firstString(entry.transcript) || null,
        ...emailMessageFields(entry),
        eventId: entry.event_name ? entry.event_id : null,
        eventName: entry.event_name || null,
        at: toTimestamp(entry.received_at || entry.sent_at || entry.at || entry.timestamp || entry.created_at),
        pending: false,
        media: mediaEntries,
        mediaKeys: mediaEntries.map((media) => media.key),
        mediaUrls: mediaEntries.map((media) => media.url).filter(Boolean),
        index,
    };
}

/**
 * Merges conversation_log and raw_message_log into one chronological list of messages.
 * Duplicates (the same text in the same direction present in both logs) collapse into a single
 * message that keeps the conversation_log role (so it can be tagged "AI Attendant" / staff) and
 * borrows the channel and the earliest timestamp from whichever entry carries them.
 */
export function mergeThreadMessages({ conversationLog = [], rawMessageLog = [], pendingReplies = [] } = {}) {
    const conversationEntries = [...(Array.isArray(conversationLog) ? conversationLog : []), ...(Array.isArray(pendingReplies) ? pendingReplies : [])]
        .map((entry, index) => normalizeConversationEntry(entry, index));
    const rawEntries = (Array.isArray(rawMessageLog) ? rawMessageLog : [])
        .map((entry, index) => normalizeRawEntry(entry, index));
    // BL-177 Step 2d (live bug): copies of one sent text that share a client_ref (optimistic bubble, staff log entry,
    // saved provider row) become ONE message before anything else — the saved row replaces the optimistic bubble,
    // never sits next to it, whichever arrived first.
    const reconciled = reconcileAll([], [...conversationEntries, ...rawEntries].filter(Boolean));

    const merged = [];
    const byKey = new Map();
    // Old template sends can sit in one log with their raw "{{n}}" body and in the other log
    // filled from contentVariables. Their display text differs, so they are also paired on the
    // raw template text — but only a filled entry with an unfilled one, from opposite logs, sent
    // within TEMPLATE_TWIN_WINDOW_MS of each other, so two genuinely different sends of the same
    // template never collapse together.
    const byTemplateKey = new Map();
    const templateKey = (entry) => (
        hasTemplatePlaceholders(entry.templateText)
            ? textDedupeKey({ ...entry, text: entry.templateText })
            : null
    );
    const isUnfilled = (entry) => entry.text === entry.templateText;
    const findTemplatePartner = (entry) => {
        const key = templateKey(entry);
        if (!key) return null;
        return (byTemplateKey.get(key) || []).find((candidate) => (
            !candidate.templatePaired
            && candidate.source !== entry.source
            && isUnfilled(candidate) !== isUnfilled(entry)
            && (candidate.at === null || entry.at === null || Math.abs(candidate.at - entry.at) <= TEMPLATE_TWIN_WINDOW_MS)
        )) || null;
    };
    const findProviderTextTwin = (entry) => merged.find((candidate) => (
        candidate.direction === entry.direction
        && !(candidate.channel === 'email' && entry.channel === 'email' && candidate.providerMessageId && entry.providerMessageId)
        && candidate.providerMessageId !== entry.providerMessageId
        && (candidate.providerMessageId || entry.providerMessageId)
        && textDedupeKey(candidate) === textDedupeKey(entry)
        && candidate.at !== null
        && entry.at !== null
        && Math.abs(candidate.at - entry.at) <= STAFF_ECHO_PAIR_WINDOW_MS
    )) || null;

    for (const entry of reconciled) {
        if (!entry || (!entry.text && entry.mediaKeys.length === 0 && entry.channel !== 'email')) continue;
        const key = dedupeKey(entry);
        let existing = byKey.get(key);
        if (!existing && entry.providerEventId && entry.clientRef && entry.direction === 'outbound') {
            // An IntelAgent-sent text whose saved row now carries the imported provider echo's event id: still the same text as
            // an older staff log entry that has no client_ref (written before client_refs were logged there).
            const candidate = byKey.get(textDedupeKey(entry));
            if (candidate && candidate.source === 'conversation_log' && !candidate.clientRef
                && (candidate.at === null || entry.at === null || Math.abs(candidate.at - entry.at) <= STAFF_ECHO_PAIR_WINDOW_MS)) {
                existing = candidate;
            }
        }
        if (!existing) existing = findProviderTextTwin(entry);
        if (!existing) {
            existing = findTemplatePartner(entry);
            if (existing) {
                existing.templatePaired = true;
                entry.templatePaired = true;
            }
        }
        if (!existing) {
            byKey.set(key, entry);
            const tKey = templateKey(entry);
            if (tKey) byTemplateKey.set(tKey, [...(byTemplateKey.get(tKey) || []), entry]);
            merged.push(entry);
            continue;
        }
        // Keep the conversation_log entry (it knows the role) but take whatever detail the
        // other log adds.
        const keeper = existing.source === 'conversation_log' ? existing : entry;
        const other = keeper === existing ? entry : existing;
        if (isUnfilled(keeper) && !isUnfilled(other)) {
            keeper.text = other.text;
            keeper.contentVariables = keeper.contentVariables || other.contentVariables;
        }
        keeper.templatePaired = keeper.templatePaired || other.templatePaired;
        keeper.channel = keeper.channel || other.channel;
        keeper.messageSource = keeper.messageSource || other.messageSource;
        keeper.staffUserId = keeper.staffUserId || other.staffUserId;
        keeper.staffName = keeper.staffName || other.staffName;
        keeper.providerEventId = keeper.providerEventId || other.providerEventId;
        keeper.providerMessageId = keeper.providerMessageId || other.providerMessageId;
        keeper.clientRef = keeper.clientRef || other.clientRef;
        keeper.retried = Boolean(keeper.retried || other.retried);
        if (other.source === 'raw_message_log' && other.channel === 'email') {
            ['emailIsRead','emailDeleted','emailOutsideFolders','emailFullText','emailSyncId'].forEach((field) => { keeper[field] = other[field]; });
        }
        keeper.provider_status = keeper.provider_status || other.provider_status;
        keeper.sendingLine = keeper.sendingLine || other.sendingLine;
        keeper.carrier = keeper.carrier || other.carrier;
        keeper.deliveryReceipts = keeper.deliveryReceipts ?? other.deliveryReceipts;
        keeper.provider_error_code = keeper.provider_error_code || other.provider_error_code;
        keeper.provider_error_message = keeper.provider_error_message || other.provider_error_message;
        ['emailSessionId', 'emailSubject', 'emailCc', 'emailBcc', 'emailTo', 'emailHtml','emailThreadId','emailMessageId','emailReferences','emailInReplyTo','emailProviderId','emailSyncId','emailFrom','emailIsRead','emailDeleted','emailOutsideFolders','emailFullText','emailSyncError'].forEach((field) => {
            if (keeper[field] === undefined && other[field] !== undefined) keeper[field] = other[field];
        });
        const mergedMedia = [...(keeper.media || []), ...(other.media || [])];
        const seenMediaKeys = new Set();
        keeper.media = mergedMedia.filter((media) => {
            if (!media?.key || seenMediaKeys.has(media.key)) return false;
            seenMediaKeys.add(media.key);
            return true;
        });
        keeper.mediaKeys = keeper.media.map((media) => media.key);
        keeper.mediaUrls = keeper.media.map((media) => media.url).filter(Boolean);
        if (other.at !== null && (keeper.at === null || other.at < keeper.at)) keeper.at = other.at;
        keeper.pending = keeper.pending && other.pending;
        byKey.set(key, keeper);
        byKey.set(dedupeKey(keeper), keeper);
        if (keeper !== existing) {
            merged.splice(merged.indexOf(existing), 1, keeper);
        }
    }

    return merged
        .filter((entry) => !entry.emailDeleted && !entry.emailOutsideFolders)
        .map((entry, order) => {
            const { templatePaired, ...rest } = entry;
            return { ...rest, text: blankUnresolvedPlaceholders(entry.text), order };
        })
        .sort((a, b) => {
            if (a.at === null && b.at === null) return a.order - b.order;
            if (a.at === null) return 1;
            if (b.at === null) return -1;
            if (a.at === b.at) return a.order - b.order;
            return a.at - b.at;
        });
}

const CHANNEL_LABELS = {
    sms: 'SMS',
    mms: 'MMS',
    whatsapp: 'WhatsApp',
    voice: 'Voice',
    email: 'Email',
    app_chat: 'App chat',
};

export function describeChannelLabel(channel) {
    if (!channel) return '';
    return CHANNEL_LABELS[channel] || channel;
}

function normalizeSessionStatus(session = {}) {
    return firstString(session?.approval_status).toLowerCase();
}

function normalizeSessionOutcome(session = {}) {
    return firstString(session?.outcome).toLowerCase();
}

export function describeThreadStatus(session = {}) {
    const status = normalizeSessionStatus(session);
    const outcome = normalizeSessionOutcome(session);
    if (status === 'escalated' || session?.requires_human_review === true || outcome === 'escalated') {
        return { key: 'handoff', label: '⇄ Handoff', className: 'border-[#F97316] text-[#F97316]' };
    }
    if (status === 'closed' || outcome === 'abandoned') {
        return { key: 'closed', label: '■ Closed', className: 'border-[#6B7280] text-[#6B7280]' };
    }
    if (status === 'resolved' || outcome === 'resolved' || outcome === 'auto_confirmed') {
        return { key: 'confirmed', label: '✓ Confirmed', className: 'border-[#22C55E] text-[#22C55E]' };
    }
    return { key: 'in_progress', label: '◔ In progress', className: 'border-[#94A3B8] text-[#94A3B8]' };
}

export function resolveLatestCustomerMessageAt(session = {}) {
    const merged = mergeThreadMessages({
        conversationLog: session?.conversation_log,
        rawMessageLog: session?.raw_message_log,
    });
    const stamps = merged
        .filter((message) => message?.direction === 'inbound')
        .map((message) => message?.at)
        .filter((value) => Number.isFinite(value));
    if (stamps.length > 0) return Math.max(...stamps);
    const fallback = toTimestamp(session?.last_inbound_at);
    return fallback ?? null;
}

export function describeChannelTagTone({
    customerLastMessageAt = null,
    now = Date.now(),
    channelActiveHours = 8,
    channelCoolingHours = 16,
} = {}) {
    if (customerLastMessageAt === null || customerLastMessageAt === undefined) return 'cool';
    const safeActiveHours = Number.isFinite(Number(channelActiveHours)) ? Number(channelActiveHours) : 8;
    const safeCoolingHours = Number.isFinite(Number(channelCoolingHours)) ? Number(channelCoolingHours) : 16;
    const activeHours = Math.max(1, safeActiveHours);
    const coolingHours = safeCoolingHours > activeHours ? safeCoolingHours : Math.max(activeHours + 1, 16);
    const ageHours = (now - customerLastMessageAt) / (60 * 60 * 1000);
    if (ageHours < activeHours) return 'active';
    if (ageHours <= coolingHours) return 'warm';
    return 'cool';
}

const DELIVERY_STATUS_LABELS = {
    queued: 'Queued',
    sent: 'Sent',
    delivered: 'Delivered',
    read: 'Read',
    failed: 'Failed',
    undelivered: 'Failed',
    unconfirmed: 'Unconfirmed',
};

// BL-177 Step 2: an imported provider send that timed out / hit a network or server error. It may still have gone out,
// so it is shown as a warning (not "failed"), is never retried automatically, and has no one-click retry.
export const UNCONFIRMED_SEND_MESSAGE = 'We couldn’t confirm this text was sent. Check delivery before resending.';

/**
 * Humanizes a provider delivery-status value for the small caption under an outbound bubble.
 * failed/undelivered append the provider's error message when one is present; any other status
 * falls back to a title-cased rendering of the raw value.
 */
export function describeDeliveryStatusLabel(providerStatus, providerErrorMessage = null) {
    const status = firstString(providerStatus).toLowerCase();
    if (!status) return '';
    const label = DELIVERY_STATUS_LABELS[status] || `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
    if ((status === 'failed' || status === 'undelivered') && firstString(providerErrorMessage)) {
        return `${label}: ${firstString(providerErrorMessage)}`;
    }
    return label;
}

/**
 * The little source tag on an outbound bubble. Inbound messages return null — they're clearly
 * the contact, so they carry no author tag.
 */
export function resolveAuthorTag(message, { staffNames = {}, sessionChannel = null } = {}) {
    if (!message || message.direction !== 'outbound') return null;
    if (message.role === 'assistant') return 'AI Assistant';
    // A text of ours that did not come from IntelAgent (the imported provider app, an API...) is "the imported provider app": the webhook never says who.
    if (message.messageSource === LEGACY_IMPORTED_SOURCE && !(message.staffUserId || message.clientRef || message.pending)) return LEGACY_IMPORTED_LABEL;
    const channel = describeChannelLabel(message.channel || sessionChannel);
    if (message.role === 'staff' || message.staffUserId || message.staffName) {
        // The staff member's current display name wins over the name stored on the message at send time
        // (older rows stored a username such as "val").
        const staffLabel = firstString(message.staffUserId ? staffNames[message.staffUserId] : '', message.staffName, message.staff_name) || 'Staff';
        const identity = staffLabel === 'Staff' ? staffLabel : `${contactInitials(staffLabel)} · ${staffLabel}`;
        return channel ? `${identity} · ${channel}` : identity;
    }
    return channel ? `Staff · ${channel}` : 'Staff';
}

/** "6d gap" / "8h gap" / "45m gap" — the relative size of a conversation break. */
export function formatGapLabel(gapMs) {
    const minutes = Math.round(gapMs / 60000);
    if (minutes < 60) return `${Math.max(minutes, 1)}m gap`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `${hours}h gap`;
    return `${Math.round(hours / 24)}d gap`;
}

/**
 * Walks the merged messages and inserts a divider wherever two consecutive entries sit more
 * than 30 minutes apart — the AI Assistant session boundary used by the thread display.
 */
export function withConversationDividers(messages = [], gapMs = CONVERSATION_GAP_MS) {
    const items = [];
    let previousAt = null;
    messages.forEach((message) => {
        if (previousAt !== null && message.at !== null && message.at - previousAt > gapMs) {
            items.push({
                kind: 'divider',
                id: `divider-${message.id}`,
                gapMs: message.at - previousAt,
                label: formatGapLabel(message.at - previousAt),
            });
        }
        if (message.at !== null) previousAt = message.at;
        items.push({ kind: 'message', ...message });
    });
    return items;
}

/** Convenience: merged messages with dividers already woven in. */
export function buildThreadItems({ conversationLog, rawMessageLog, pendingReplies, gapMs = CONVERSATION_GAP_MS } = {}) {
    return withConversationDividers(mergeThreadMessages({ conversationLog, rawMessageLog, pendingReplies }), gapMs);
}

/** Display name for a thread: CRM contact name, else account name, else email, else phone. */
export function resolveContactName(session, account, contact = null, myContact = null) {
    // PR D: a company contact's name (then the company account's) always wins; the viewer's OWN My Contact only fills in when nothing in the company names this person.
    return firstString(contact?.name, account?.account_name, myContact?.name, session?.contact_email, session?.contact_phone) || 'Unknown contact';
}

/** Phone/email line under the contact name. */
export function resolveContactAddress(session) {
    return firstString(session?.contact_phone, session?.contact_email);
}

function resolveThreadContactKey(session) {
    const channel = firstString(session?.channel).toLowerCase();
    if (channel === 'email') {
        const email = firstString(session?.contact_email).toLowerCase();
        if (email) return `email:${email}`;
    }

    const phone = normalizePhoneAddress(firstString(session?.contact_phone));
    if (phone) return `phone:${phone}`;

    const email = firstString(session?.contact_email).toLowerCase();
    if (email) return `email:${email}`;

    const address = resolveContactAddress(session);
    if (address) return `address:${address.toLowerCase()}`;

    return '';
}

function threadGroupKey(session) {
    const orgId = firstString(session?.org_id, session?.tenant_id);
    if (session?.channel === 'app_chat') return `${orgId}::app_chat:${session.account_id || session.id}`;
    const contactKey = resolveThreadContactKey(session);
    if (!contactKey) return `session:${session?.id || ''}`;
    return `${orgId || 'org:unknown'}::${contactKey}`;
}

export function contactInitials(name) {
    const cleaned = firstString(name);
    if (!cleaned) return '?';
    const words = cleaned.split(/[\s@._-]+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

/** Most recent message text, trimmed to a single preview line. */
export function threadPreview(session, maxLength = 90) {
    const messages = mergeThreadMessages({
        conversationLog: session?.conversation_log,
        rawMessageLog: session?.raw_message_log,
    });
    const last = messages[messages.length - 1];
    if (!last) return '';
    const text = toPlainThreadText(last.text).replace(/\s+/g, ' ').trim();
    const prefix = last.role === 'assistant'
        ? 'AI: '
        : (last.direction === 'outbound' ? 'You: ' : '');
    const withPrefix = `${prefix}${text}`;
    return withPrefix.length > maxLength ? `${withPrefix.slice(0, maxLength - 1)}…` : withPrefix;
}

/** Newest-activity sort key for the thread list. */
export function threadActivityAt(session) {
    const messages = mergeThreadMessages({
        conversationLog: session?.conversation_log,
        rawMessageLog: session?.raw_message_log,
    });
    const stamps = messages.map((message) => message.at).filter((value) => value !== null);
    const candidates = [
        ...stamps,
        toTimestamp(session?.last_inbound_at),
        toTimestamp(session?.updated_at),
        toTimestamp(session?.created_at),
    ].filter((value) => value !== null && value !== undefined);
    return candidates.length ? Math.max(...candidates) : 0;
}

function splitNameForSort(name) {
    const cleaned = firstString(name).replace(/\s+/g, ' ');
    if (!cleaned) return { first: '', last: '' };
    const parts = cleaned.split(' ');
    if (parts.length === 1) return { first: parts[0], last: parts[0] };
    return {
        first: parts.slice(0, -1).join(' '),
        last: parts[parts.length - 1],
    };
}

function compareText(first, second) {
    return first.localeCompare(second, undefined, { sensitivity: 'base', numeric: true });
}

function sortThreads(threads, threadSortOrder = 'recent') {
    return threads.slice().sort((a, b) => {
        if (threadSortOrder === 'first_name' || threadSortOrder === 'last_name') {
            const aParts = splitNameForSort(a.name);
            const bParts = splitNameForSort(b.name);
            const primary = threadSortOrder === 'first_name'
                ? compareText(aParts.first, bParts.first)
                : compareText(aParts.last, bParts.last);
            if (primary !== 0) return primary;
            const secondary = threadSortOrder === 'first_name'
                ? compareText(aParts.last, bParts.last)
                : compareText(aParts.first, bParts.first);
            if (secondary !== 0) return secondary;
        }
        if (b.activityAt !== a.activityAt) return b.activityAt - a.activityAt;
        return compareText(a.id || '', b.id || '');
    });
}

/** Full merged conversation text for client-side keyword search. */
export function threadConversationText(session) {
    return mergeThreadMessages({
        conversationLog: session?.conversation_log,
        rawMessageLog: session?.raw_message_log,
    }).map((message) => toPlainThreadText(message.text)).join(' ');
}

function contactPhoneDigits(value) {
    const address = typeof value === 'string'
        ? value
        : firstString(value?.phone, value?.number, value?.address, value?.value);
    return normalizePhoneAddress(address).replace(/\D/g, '');
}

function contactSearchAddresses(contact, field) {
    const values = Array.isArray(contact?.[field]) ? contact[field] : [contact?.[field]];
    return values.map((value) => (
        typeof value === 'string'
            ? value
            : firstString(value?.email, value?.phone, value?.number, value?.address, value?.value)
    )).filter(Boolean);
}

function addToIndex(index, key, value) {
    if (!key) return;
    if (!index.has(key)) index.set(key, []);
    index.get(key).push(value);
}

export function buildContactSearchIndexes(contacts = []) {
    const byAccount = new Map();
    const byAddress = new Map();
    contacts.forEach((contact) => {
        if (!contact) return;
        if (contact.primary_account_id) addToIndex(byAccount, contact.primary_account_id, contact);
        const orgId = firstString(contact.org_id);
        if (!orgId) return;
        contactSearchAddresses(contact, 'phones').forEach((phone) => {
            const digits = contactPhoneDigits(phone);
            if (digits) addToIndex(byAddress, `${orgId}\u0000phone:${digits}`, contact);
        });
        contactSearchAddresses(contact, 'emails').forEach((email) => {
            const normalized = email.trim().toLowerCase();
            if (normalized) addToIndex(byAddress, `${orgId}\u0000email:${normalized}`, contact);
        });
    });
    return { byAccount, byAddress };
}

export function searchableThreadWords(values) {
    return values
        .filter((value) => typeof value === 'string' && value)
        .join(' ')
        .toLocaleLowerCase()
        .split(/[^\p{L}\p{N}@.+_-]+/u)
        .filter(Boolean);
}

// Comms next pass (1.12.214-beta): hybrid search. A term that starts a word ("air" → "Airport") is a PREFIX match and
// ranks first, so the typeahead stays clean. A term of 3+ characters may also match inside a word ("port" → "airport",
// "1234" → "DL1234"): a CONTAINS match, ranked below. Phone digits inside a number also need 3+ digits.
export const SEARCH_RANK_NONE = 0;
export const SEARCH_RANK_CONTAINS = 1;
export const SEARCH_RANK_PREFIX = 2;
export const SEARCH_CONTAINS_MIN_CHARS = 3;

export function searchNeedles(term) {
    return firstString(term).toLocaleLowerCase().split(/\s+/).filter(Boolean);
}

function threadSearchWords(thread) {
    return thread?.searchWords || searchableThreadWords([
        thread?.name,
        ...(Array.isArray(thread?.searchNames) ? thread.searchNames : []),
        ...(Array.isArray(thread?.searchEmails) ? thread.searchEmails : []),
        ...(Array.isArray(thread?.searchSubjects) ? thread.searchSubjects : []),
        thread?.accountName,
        thread?.session?.contact_phone,
        thread?.session?.contact_email,
        thread?.preview,
        thread?.searchText,
    ]);
}

function threadSearchPhoneDigits(thread) {
    return thread?.searchPhoneDigits
        || [thread?.session?.contact_phone, ...(Array.isArray(thread?.searchPhones) ? thread.searchPhones : [])]
            .map(contactPhoneDigits).filter(Boolean);
}

/** How one search term matches one word list: PREFIX beats CONTAINS (3+ chars only) beats NONE. */
export function searchTermRank(words, needle, phoneDigits = []) {
    if (!needle) return SEARCH_RANK_PREFIX;
    let contains = false;
    const allowContains = needle.length >= SEARCH_CONTAINS_MIN_CHARS;
    for (let i = 0; i < words.length; i += 1) {
        const word = words[i];
        if (word.startsWith(needle)) return SEARCH_RANK_PREFIX;
        if (allowContains && !contains && word.includes(needle)) contains = true;
    }
    if (/^\+?[\d\s().-]+$/.test(needle)) {
        const digits = needle.replace(/\D/g, '');
        if (digits.length > 0) {
            for (let i = 0; i < phoneDigits.length; i += 1) {
                if (phoneDigits[i].startsWith(digits)) return SEARCH_RANK_PREFIX;
                if (!contains && digits.length >= SEARCH_CONTAINS_MIN_CHARS && phoneDigits[i].includes(digits)) contains = true;
            }
        }
    }
    return contains ? SEARCH_RANK_CONTAINS : SEARCH_RANK_NONE;
}

/**
 * Client-side keyword search rank, AND across terms: PREFIX when every term starts a word, CONTAINS when every term
 * matches but at least one only mid-word, NONE when any term misses. Uses the precomputed per-thread words when present.
 */
export function threadSearchRank(thread, term) {
    const needles = searchNeedles(term);
    if (needles.length === 0) return SEARCH_RANK_PREFIX;
    const words = threadSearchWords(thread);
    const phoneDigits = threadSearchPhoneDigits(thread);
    let rank = SEARCH_RANK_PREFIX;
    for (let i = 0; i < needles.length; i += 1) {
        const termRank = searchTermRank(words, needles[i], phoneDigits);
        if (termRank === SEARCH_RANK_NONE) return SEARCH_RANK_NONE;
        if (termRank < rank) rank = termRank;
    }
    return rank;
}

/** True when the thread matches every search term (prefix, or contains for 3+ character terms). */
export function threadMatchesSearch(thread, term) {
    return threadSearchRank(thread, term) !== SEARCH_RANK_NONE;
}

/**
 * Filters + ranks in one pass: prefix matches first, then contains matches, each keeping the incoming order (stable).
 * `predicate` runs before the search so cheap filters (tab, channel) skip the word scan.
 */
export function rankThreadsBySearch(threads = [], term = '', predicate = null) {
    const prefix = [];
    const contains = [];
    const needlesPresent = searchNeedles(term).length > 0;
    threads.forEach((thread) => {
        if (predicate && !predicate(thread)) return;
        const rank = needlesPresent ? threadSearchRank(thread, term) : SEARCH_RANK_PREFIX;
        if (rank === SEARCH_RANK_PREFIX) prefix.push(thread);
        else if (rank === SEARCH_RANK_CONTAINS) contains.push(thread);
    });
    return prefix.concat(contains);
}

function mergeThreadSessions(sessions = []) {
    const sorted = sessions.slice().sort((a, b) => threadActivityAt(b) - threadActivityAt(a));
    const mostRecent = sorted[0] || {};
    return {
        ...mostRecent,
        conversation_log: sessions.flatMap((session) => (Array.isArray(session?.conversation_log) ? session.conversation_log : [])),
        raw_message_log: sessions.flatMap((session) => (Array.isArray(session?.raw_message_log) ? session.raw_message_log : [])),
    };
}

/**
 * Builds the left-pane rows: one per contact within the org, sorted by the display preference, each carrying
 * the derived name/initials/preview/tags the row renders.
 */
export function buildThreadList({
    sessions = [],
    accountsById = new Map(),
    contacts = [],
    contactSearchIndexes = null,
    resolveContactForSession = null,
    resolveMyContactForSession = null,   // BL-177 PR D: the signed-in person's own My Contact, or null
    threadSortOrder = 'recent',
} = {}) {
    const groups = new Map();
    const contactIndexes = contactSearchIndexes || buildContactSearchIndexes(contacts);
    sessions.filter(Boolean).forEach((session) => {
        const key = threadGroupKey(session);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(session);
    });

    const threads = [...groups.values()]
        .map((group) => {
            const session = mergeThreadSessions(group);
            const account = accountsById.get ? accountsById.get(session.account_id) : accountsById[session.account_id];
            const contact = typeof resolveContactForSession === 'function'
                ? resolveContactForSession(session, account)
                : null;
            const accountName = firstString(account?.account_name) || 'No account';
            const myContact = !contact && typeof resolveMyContactForSession === 'function' ? resolveMyContactForSession(session) : null;
            const orgId = firstString(session.org_id, session.tenant_id);
            const matchedContacts = new Set(
                session.account_id ? contactIndexes.byAccount.get(session.account_id) || [] : [],
            );
            if (orgId) {
                const phoneKey = `${orgId}\u0000phone:${contactPhoneDigits(session.contact_phone)}`;
                const emailKey = `${orgId}\u0000email:${firstString(session.contact_email).trim().toLowerCase()}`;
                groupParticipants(session.contact_phone).forEach((phone) => {
                    const phoneDigits = contactPhoneDigits(phone);
                    const localDigits = phoneDigits.replace(/^1(?=\d{10}$)/, '');
                    [...(contactIndexes.byAddress.get(`${orgId}\u0000phone:${phoneDigits}`) || []), ...(contactIndexes.byAddress.get(`${orgId}\u0000phone:${localDigits}`) || [])].forEach((candidate) => matchedContacts.add(candidate));
                });
                (contactIndexes.byAddress.get(phoneKey) || []).forEach((candidate) => matchedContacts.add(candidate));
                (contactIndexes.byAddress.get(emailKey) || []).forEach((candidate) => matchedContacts.add(candidate));
            }
            if (contact && (!orgId || contact.org_id === orgId)) matchedContacts.add(contact);
            const searchableContacts = [...matchedContacts].filter((candidate) => (
                !orgId || candidate.org_id === orgId
            ));
            // Comms final pass (#759 follow-up): a CRM contact matched by this conversation's phone or email names the thread even
            // when the conversation has no account (it used to show a bare number under "Not in CRM", and "Add to CRM" could make
            // a duplicate). The resolver's own match wins; otherwise the first contact the address index found.
            const addressContact = contact || (orgId && !account ? [
                ...(contactIndexes.byAddress.get(`${orgId}\u0000phone:${contactPhoneDigits(session.contact_phone)}`) || []),
                ...(contactIndexes.byAddress.get(`${orgId}\u0000email:${firstString(session.contact_email).trim().toLowerCase()}`) || []),
            ].find((candidate) => candidate && (!orgId || candidate.org_id === orgId)) : null) || null;
            const contactAccount = !account && addressContact?.primary_account_id && accountsById
                ? (accountsById.get ? accountsById.get(addressContact.primary_account_id) : accountsById[addressContact.primary_account_id]) || null
                : null;
            const memberPhones = groupParticipants(session.contact_phone);
            const memberNames = memberPhones.map((phone) => [...matchedContacts].find((candidate) => contactSearchAddresses(candidate, 'phones').some((item) => contactPhoneDigits(item).replace(/^1(?=\d{10}$)/, '') === contactPhoneDigits(phone).replace(/^1(?=\d{10}$)/, '')))?.name || phone);
            const name = String(session.external_thread_id || '').startsWith('conversations:')
                ? `${memberNames.slice(0, 2).map((item) => item.split(' ')[0]).join(', ')}${memberNames.length > 2 ? ` +${memberNames.length - 2}` : ''}`
                : resolveContactName(session, account, addressContact, addressContact ? null : myContact);
            const threadMessages = mergeThreadMessages({
                conversationLog: session.conversation_log,
                rawMessageLog: session.raw_message_log,
            });
            const searchNames = [...new Set([
                name,
                account?.account_name,
                contact?.name,
                ...searchableContacts.map((candidate) => candidate.name),
            ].map((value) => firstString(value)).filter(Boolean))];
            const searchSubjects = [...new Set([
                session.email_subject,
                ...threadMessages.map((message) => message.emailSubject),
            ].map((value) => firstString(value)).filter(Boolean))];
            const searchPhones = [...new Set([
                session.contact_phone,
                ...memberPhones,
                ...searchableContacts.flatMap((candidate) => contactSearchAddresses(candidate, 'phones')),
            ].map((value) => firstString(value)).filter(Boolean))];
            const searchEmails = [...new Set([
                session.contact_email,
                ...searchableContacts.flatMap((candidate) => contactSearchAddresses(candidate, 'emails')),
            ].map((value) => firstString(value).toLocaleLowerCase()).filter(Boolean))];
            const searchText = threadConversationText(session);
            const preview = threadPreview(session);
            const searchWords = searchableThreadWords([
                name,
                ...searchNames,
                ...searchEmails,
                ...searchSubjects,
                accountName,
                ...searchPhones,
                preview,
                searchText,
            ]);
            return {
                id: session.id,
                session,
                name,
                searchNames,
                searchContactIds: searchableContacts.map((candidate) => candidate.id).filter(Boolean),
                searchEmails,
                searchPhones,
                searchSubjects,
                searchWords,
                searchPhoneDigits: searchPhones.map(contactPhoneDigits).filter(Boolean),
                initials: contactInitials(name),
                accountId: session.account_id || null,
                accountName,
                contactId: addressContact?.id || null,
                // the person for a group header (an account named by its phone number shows this name instead)
                contactName: firstString(addressContact?.name, searchableContacts[0]?.name),
                contactAccountId: contactAccount?.id || null,
                contactAccountName: firstString(contactAccount?.account_name),
                address: resolveContactAddress(session),
                preview,
                searchText,
                activityAt: threadActivityAt(session),
                channel: session.channel || null,
                status: session.approval_status || 'pending',
                statusDisplay: describeThreadStatus(session),
                customerLastMessageAt: resolveLatestCustomerMessageAt(session),
                archivedAt: session.archived_at || null,
            };
        });
    return sortThreads(threads, threadSortOrder);
}

/** Distinct staffUserId values referenced by a session's conversation_log. */
export function collectStaffUserIds(sessions = []) {
    const ids = new Set();
    (Array.isArray(sessions) ? sessions : []).forEach((session) => {
        const log = Array.isArray(session?.conversation_log) ? session.conversation_log : [];
        log.forEach((entry) => {
            const id = firstString(entry?.staffUserId, entry?.staff_user_id, entry?.userId, entry?.user_id);
            if (id) ids.add(id);
        });
        (Array.isArray(session?.raw_message_log) ? session.raw_message_log : []).forEach((entry) => {
            const id = firstString(entry?.sent_by);
            if (id) ids.add(id);
        });
    });
    return [...ids];
}
